// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { SessionStatus } from '../types/interview';

/**
 * Configuration for session status display messages
 */
interface StatusMessageConfig {
  icon: string;
  title: string;
  message: string;
  actionText?: string;
}

/**
 * Get user-friendly display information for interview session status
 * @param status - The session status from API response
 * @returns Status message configuration with icon, title, message, and severity
 */
export function getSessionStatusMessage(status: SessionStatus): StatusMessageConfig {
  const statusMessages: Record<SessionStatus, StatusMessageConfig> = {
    VALID: {
      icon: '✓',
      title: 'Session Valid',
      message: 'Your interview session is ready. Please proceed with the identity verification.'
    },

    NOT_FOUND: {
      icon: '⚠️',
      title: 'Invalid Interview Link',
      message:
        'We could not find an interview session associated with this link. Please verify that you are using the correct link provided in your invitation email.',
      actionText: 'If you believe this is an error, please contact the hiring team or'
    },

    EXPIRED: {
      icon: '⚠️',
      title: 'Interview Link Expired',
      message:
        'This interview link has expired and is no longer valid. Interview invitations are typically valid for a limited time period.',
      actionText: 'Please contact the hiring team to request a new interview link, or'
    },

    COMPLETED: {
      icon: '⚠️',
      title: 'Interview Already Completed',
      message:
        'Our records show that you have already completed this interview. Thank you for participating! The hiring team will review your responses and contact you regarding the next steps.',
      actionText: 'If you have any questions, please'
    },

    CANCELLED: {
      icon: '⚠️',
      title: 'Interview Cancelled',
      message:
        'This interview has been cancelled by the hiring team. We apologize for any inconvenience this may cause.',
      actionText: 'For more information about this decision or to discuss rescheduling, please'
    },

    ACTIVE_SESSION_EXISTS: {
      icon: '⚠️',
      title: 'Active Session Detected',
      message:
        'You currently have another active interview session open. Please complete or close your existing session before starting a new one. Only one interview session can be active at a time.',
      actionText: 'If you are experiencing technical difficulties, please'
    },

    IN_PROGRESS: {
      icon: '⚠️',
      title: 'Interview In Progress',
      message:
        'This interview is currently in progress. If you were disconnected, you may be able to resume from where you left off.',
      actionText: 'If you are unable to resume or need assistance, please'
    },

    INVALID: {
      icon: '⚠️',
      title: 'Invalid Session',
      message:
        'The interview session link is not valid. This may occur if the link is malformed or has been tampered with.',
      actionText: 'Please use the original link from your invitation email, or'
    }
  };

  return statusMessages[status] || statusMessages.INVALID;
}

/**
 * Check if the session status allows proceeding with the interview
 * @param status - The session status from API response
 * @returns true if user can proceed, false otherwise
 */
export function canProceedWithInterview(status: SessionStatus): boolean {
  return status === 'VALID' || status === 'IN_PROGRESS';
}
